import React, { useEffect, useMemo, useState } from 'react'
import {
  Alert,
  Avatar,
  Backdrop,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  LinearProgress,
  MenuItem,
  Paper,
  Stack,
  FormControlLabel,
  Switch,
  TextField,
  Typography,
} from '@mui/material'
import {
  Add,
  Apartment,
  Delete,
  Devices,
  Edit,
  Phone,
  Refresh,
  RestartAlt,
  Visibility,
  VpnKey,
  Description,
  UploadFile,
  WarningAmber,
} from '@mui/icons-material'
import { customerService, customerReportBrandingService, deviceService, errorLogService, extractApiError } from '../services/api'
import ReportBrandingPanel, { BrandingKind } from '../components/ReportBrandingPanel'
import {
  CreateCustomerPayload,
  CreateDevicePayload,
  CustomerDeviceRecord,
  CustomerRecord,
  CustomerWithDevicesRecord,
  DeviceRecord,
  ErrorLogRecord,
} from '../types'
import EditDeviceDialog from '../components/EditDeviceDialog'

type Feedback = {
  severity: 'success' | 'error' | 'info'
  message: string
}

type CustomerFormState = {
  name: string
  location: string
  phone: string
  notes: string
  ownerName: string
  specialization: string
  reportHeaderHeightMm: number
  reportFooterHeightMm: number
  receiptHeaderHeightMm: number
  enableReps: boolean
  enableInstallments: boolean
}

type DeviceFormState = {
  type: 'Main' | 'Sub' | 'Mobile' | 'MeraApp' | 'Api'
  mainDeviceId: string
  deviceCode: string
  version: string
  subscriptionType: 'Trial' | 'Monthly' | 'Yearly' | 'Permanent'
  subscriptionDurationMonths: number
  notes: string
  meraApiUrl: string
  useIndependentReportBranding: boolean
  createLinkedApi: boolean
  dbPasswordOverride: string
  apiHostingKind: 'Windows' | 'Host'
}

const getDeviceTypeLabel = (type: string) => {
  switch (type) {
    case 'Main':
      return 'جهاز رئيسي'
    case 'Sub':
      return 'جهاز فرعي'
    case 'Mobile':
      return 'موبايل Mera Route'
    case 'MeraApp':
      return 'Mera موبايل'
    case 'Api':
      return 'API'
    default:
      return type
  }
}

const emptyCustomerForm: CustomerFormState = {
  name: '',
  location: '',
  phone: '',
  notes: '',
  ownerName: '',
  specialization: '',
  reportHeaderHeightMm: 35,
  reportFooterHeightMm: 25,
  receiptHeaderHeightMm: 30,
  enableReps: true,
  enableInstallments: true,
}

const emptyDeviceForm: DeviceFormState = {
  type: 'Main',
  mainDeviceId: '',
  deviceCode: '',
  version: '1.0.0',
  subscriptionType: 'Trial',
  subscriptionDurationMonths: 1,
  notes: '',
  meraApiUrl: 'http://localhost:5078',
  useIndependentReportBranding: false,
  createLinkedApi: true,
  dbPasswordOverride: '',
  apiHostingKind: 'Windows',
}

/** عنوان يصل إليه الجهاز لـ MeraSystemApi — ليس 0.0.0.0 (ذلك لربط السيرفر فقط). */
const DEFAULT_MERA_API_URL_DESKTOP = 'http://localhost:5078'
const DEFAULT_MERA_API_URL_MOBILE = 'http://192.168.0.100:5078'

const defaultMeraApiUrlForType = (type: DeviceFormState['type']) =>
  type === 'Mobile' || type === 'MeraApp'
    ? DEFAULT_MERA_API_URL_MOBILE
    : DEFAULT_MERA_API_URL_DESKTOP

const isStockMeraApiUrl = (url: string) => {
  const u = (url || '').trim().toLowerCase()
  return !u
    || u === DEFAULT_MERA_API_URL_DESKTOP.toLowerCase()
    || u === DEFAULT_MERA_API_URL_MOBILE.toLowerCase()
    || u === 'http://0.0.0.0:5078'
    || u === 'http://127.0.0.1:5078'
}

const unwrapData = <T,>(response: any): T => (response?.data ?? response) as T

const getStatusLabel = (status: CustomerDeviceRecord['status']) => {
  switch (status) {
    case 'Active':
      return 'مفعل'
    case 'Inactive':
      return 'بانتظار التفعيل'
    case 'Suspended':
      return 'موقوف'
    default:
      return status
  }
}

const formatDate = (value?: string | null) => {
  if (!value) return 'غير متوفر'
  return new Date(value).toLocaleString('ar-IQ', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

const getSubscriptionLabel = (type: CustomerDeviceRecord['subscriptionType'], months?: number | null) => {
  switch (type) {
    case 'Trial':
      return `تجريبي${months ? ` ${months} شهر` : ''}`
    case 'Monthly':
      return 'شهري'
    case 'Yearly':
      return 'سنوي'
    case 'Permanent':
      return 'دائمي'
    default:
      return type
  }
}

const getSubscriptionStateLabel = (state: CustomerDeviceRecord['subscriptionState']) => {
  switch (state) {
    case 'PendingActivation':
      return 'بانتظار التفعيل'
    case 'Active':
      return 'فعال'
    case 'ExpiringSoon':
      return 'قارب على الانتهاء'
    case 'GracePeriod':
      return 'فترة سماح'
    case 'Expired':
      return 'منتهي'
    case 'Permanent':
      return 'دائمي'
    default:
      return state
  }
}

const getSubscriptionStateColor = (state: CustomerDeviceRecord['subscriptionState']) => {
  switch (state) {
    case 'Active':
    case 'Permanent':
      return 'success'
    case 'ExpiringSoon':
    case 'PendingActivation':
      return 'warning'
    case 'GracePeriod':
      return 'info'
    case 'Expired':
      return 'error'
    default:
      return 'default'
  }
}

const MAX_AGREEMENT_BYTES = 10 * 1024 * 1024

const isPlaceholderDeviceCode = (deviceCode?: string | null) =>
  !!deviceCode && /^(MAIN|SUB|MOB|APP|API)-/i.test(deviceCode.trim())

/** بانتظار التفعيل وغير مربوط ببصمة حقيقية → يمكن حذفه */
const isUnlinkedDevice = (device: CustomerDeviceRecord) =>
  device.status === 'Inactive' && isPlaceholderDeviceCode(device.deviceCode)

/** مربوط ببصمة (مفعّل أو بصمة حقيقية) → يمكن فك الربط */
const canResetDeviceBinding = (device: CustomerDeviceRecord) =>
  device.status === 'Active'
  || device.status === 'Suspended'
  || (!!device.deviceCode && !isPlaceholderDeviceCode(device.deviceCode))

const validateAgreementFile = (file: File): string | null => {
  if (!file.name.toLowerCase().endsWith('.pdf') && file.type !== 'application/pdf') {
    return 'يُقبل ملف PDF فقط'
  }
  if (file.size > MAX_AGREEMENT_BYTES) {
    return 'حجم الملف يتجاوز 10 MB'
  }
  return null
}

export default function CustomersPage() {
  const [customers, setCustomers] = useState<CustomerRecord[]>([])
  const [selectedCustomer, setSelectedCustomer] = useState<CustomerWithDevicesRecord | null>(null)
  const [selectedCustomerId, setSelectedCustomerId] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [detailLoading, setDetailLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [feedback, setFeedback] = useState<Feedback | null>(null)
  const [latestCreatedDevice, setLatestCreatedDevice] = useState<DeviceRecord | null>(null)
  const [customerErrors, setCustomerErrors] = useState<ErrorLogRecord[]>([])

  const [customerDialogOpen, setCustomerDialogOpen] = useState(false)
  const [detailDialogOpen, setDetailDialogOpen] = useState(false)
  const [deviceDialogOpen, setDeviceDialogOpen] = useState(false)
  const [editingDevice, setEditingDevice] = useState<CustomerDeviceRecord | null>(null)
  const [editingCustomerId, setEditingCustomerId] = useState<string | null>(null)

  const [customerForm, setCustomerForm] = useState<CustomerFormState>(emptyCustomerForm)
  const [deviceForm, setDeviceForm] = useState<DeviceFormState>(emptyDeviceForm)
  const [agreementFile, setAgreementFile] = useState<File | null>(null)
  const [agreementUploadProgress, setAgreementUploadProgress] = useState<number | null>(null)
  const [agreementUploadLabel, setAgreementUploadLabel] = useState('')
  const agreementInputRef = React.useRef<HTMLInputElement>(null)

  const isUploadingAgreement = agreementUploadProgress !== null

  const fetchCustomers = async () => {
    try {
      setLoading(true)
      const response = await customerService.getAll()
      setCustomers(unwrapData<CustomerRecord[]>(response) ?? [])
    } catch (error: any) {
      setFeedback({ severity: 'error', message: error?.response?.data?.message || 'فشل في تحميل العملاء' })
      setCustomers([])
    } finally {
      setLoading(false)
    }
  }

  const loadCustomerDetails = async (customerId: string, openDialog = true) => {
    try {
      setSelectedCustomerId(customerId)
      setDetailLoading(true)
      if (openDialog) setDetailDialogOpen(true)
      const [customerResponse, errorsResponse] = await Promise.all([
        customerService.getWithDevices(customerId),
        errorLogService.getByCustomer(customerId, 20),
      ])
      setSelectedCustomer(unwrapData<CustomerWithDevicesRecord>(customerResponse))
      setCustomerErrors(unwrapData<ErrorLogRecord[]>(errorsResponse) ?? [])
    } catch (error: any) {
      setFeedback({ severity: 'error', message: error?.response?.data?.message || 'فشل في تحميل تفاصيل العميل' })
    } finally {
      setDetailLoading(false)
    }
  }

  useEffect(() => {
    fetchCustomers()
  }, [])

  const filteredCustomers = useMemo(() => {
    const query = searchTerm.trim().toLowerCase()
    if (!query) return customers

    return customers.filter((customer) =>
      [customer.name, customer.location, customer.phone || '', customer.notes || '']
        .join(' ')
        .toLowerCase()
        .includes(query)
    )
  }, [customers, searchTerm])

  const mainDevices = selectedCustomer?.devices.filter((device) => device.type === 'Main') ?? []
  const subDevices = selectedCustomer?.devices.filter((device) => device.type === 'Sub') ?? []
  const mobileDevices = selectedCustomer?.devices.filter((device) => device.type === 'Mobile') ?? []
  const meraAppDevices = selectedCustomer?.devices.filter((device) => device.type === 'MeraApp') ?? []
  const apiDevices = selectedCustomer?.devices.filter((device) => device.type === 'Api') ?? []

  const openCreateCustomerDialog = () => {
    setEditingCustomerId(null)
    setCustomerForm(emptyCustomerForm)
    setAgreementFile(null)
    setCustomerDialogOpen(true)
  }

  const openEditCustomerDialog = (customer: CustomerRecord) => {
    setEditingCustomerId(customer.id)
    setCustomerForm({
      name: customer.name,
      location: customer.location,
      phone: customer.phone || '',
      notes: customer.notes || '',
      ownerName: customer.ownerName || '',
      specialization: customer.specialization || '',
      reportHeaderHeightMm: customer.reportHeaderHeightMm ?? 35,
      reportFooterHeightMm: customer.reportFooterHeightMm ?? 25,
      receiptHeaderHeightMm: customer.receiptHeaderHeightMm ?? 30,
      enableReps: customer.enableReps !== false,
      enableInstallments: customer.enableInstallments !== false,
    })
    setAgreementFile(null)
    setCustomerDialogOpen(true)
  }

  const submitCustomer = async () => {
    const payload: CreateCustomerPayload = {
      name: customerForm.name.trim(),
      location: customerForm.location.trim(),
      phone: customerForm.phone.trim() || undefined,
      notes: customerForm.notes.trim() || undefined,
      ownerName: customerForm.ownerName.trim() || undefined,
      specialization: customerForm.specialization.trim() || undefined,
      reportHeaderHeightMm: customerForm.reportHeaderHeightMm,
      reportFooterHeightMm: customerForm.reportFooterHeightMm,
      receiptHeaderHeightMm: customerForm.receiptHeaderHeightMm,
      enableReps: customerForm.enableReps,
      enableInstallments: customerForm.enableInstallments,
    }

    if (!payload.name || !payload.location) {
      setFeedback({ severity: 'error', message: 'اسم العميل والموقع مطلوبان' })
      return
    }

    try {
      setSaving(true)
      let customerId = editingCustomerId
      if (editingCustomerId) {
        await customerService.update(editingCustomerId, payload)
        setFeedback({ severity: 'success', message: 'تم تحديث العميل بنجاح' })
      } else {
        const created = unwrapData<CustomerRecord>(await customerService.create(payload))
        customerId = created.id
        setFeedback({ severity: 'success', message: 'تم إنشاء العميل بنجاح' })
      }

      if (agreementFile && customerId) {
        const validationError = validateAgreementFile(agreementFile)
        if (validationError) {
          setFeedback({ severity: 'error', message: validationError })
          return
        }

        setAgreementUploadLabel(`جاري رفع ${agreementFile.name}...`)
        setAgreementUploadProgress(1)
        await customerService.uploadLicenseAgreement(customerId, agreementFile, (percent) => {
          setAgreementUploadProgress(percent)
        })
        setAgreementUploadProgress(null)
        setAgreementUploadLabel('')
        setFeedback({ severity: 'success', message: editingCustomerId ? 'تم تحديث العميل ورفع الاتفاقية' : 'تم إنشاء العميل ورفع الاتفاقية' })
      } else if (!editingCustomerId) {
        setFeedback({ severity: 'info', message: 'تم إنشاء العميل — لم تُرفع اتفاقية الترخيص بعد (ستظهر باللون الأحمر)' })
      }

      setCustomerDialogOpen(false)
      setCustomerForm(emptyCustomerForm)
      setAgreementFile(null)
      await fetchCustomers()
    } catch (error: unknown) {
      setAgreementUploadProgress(null)
      setAgreementUploadLabel('')
      setFeedback({ severity: 'error', message: extractApiError(error, 'تعذر حفظ بيانات العميل') })
    } finally {
      setSaving(false)
    }
  }

  const deleteCustomer = async (customer: CustomerRecord) => {
    if (customer.deviceCount > 0) {
      setFeedback({ severity: 'error', message: 'لا يمكن حذف عميل لديه أجهزة — احذف الأجهزة غير المرتبطة أولاً' })
      return
    }
    if (!window.confirm(`هل تريد حذف العميل «${customer.name}»؟ لا يمكن التراجع.`)) return

    try {
      await customerService.delete(customer.id)
      setFeedback({ severity: 'success', message: 'تم حذف العميل بنجاح' })
      setDetailDialogOpen(false)
      setSelectedCustomer(null)
      setSelectedCustomerId(null)
      await fetchCustomers()
    } catch (error: unknown) {
      setFeedback({ severity: 'error', message: extractApiError(error, 'تعذر حذف العميل') })
    }
  }

  const deleteUnlinkedDevice = async (device: CustomerDeviceRecord, customerId: string) => {
    if (!isUnlinkedDevice(device)) {
      setFeedback({ severity: 'error', message: 'يمكن حذف الأجهزة «بانتظار التفعيل» فقط (غير المربوطة ببصمة) — فك الربط أولاً' })
      return
    }
    if (!window.confirm(`حذف ${getDeviceTypeLabel(device.type)} «${device.activationCode}»؟\nلا يمكن التراجع.`)) return

    try {
      setSaving(true)
      await deviceService.delete(device.id)
      setFeedback({ severity: 'success', message: 'تم حذف الجهاز بنجاح' })
      await fetchCustomers()
      await loadCustomerDetails(customerId, false)
    } catch (error: unknown) {
      setFeedback({ severity: 'error', message: extractApiError(error, 'تعذر حذف الجهاز') })
    } finally {
      setSaving(false)
    }
  }

  const resetDeviceBinding = async (device: CustomerDeviceRecord, customerId: string) => {
    if (!canResetDeviceBinding(device)) {
      setFeedback({ severity: 'info', message: 'الجهاز غير مربوط ببصمة — لا حاجة لفك الربط' })
      return
    }
    const confirmed = window.confirm(
      `فك ربط ${getDeviceTypeLabel(device.type)}؟\n\n` +
        `سيُبقى رمز التفعيل: ${device.activationCode}\n` +
        'ويمكن إعادة التفعيل لاحقاً (مثلاً على سب دومين آخر أو بعد إعادة الرفع).',
    )
    if (!confirmed) return
    try {
      setSaving(true)
      await deviceService.resetActivation(device.id)
      setFeedback({ severity: 'success', message: 'تم فك ربط الجهاز. يمكن إعادة التفعيل بنفس الرمز.' })
      await loadCustomerDetails(customerId, false)
    } catch (error: unknown) {
      setFeedback({ severity: 'error', message: extractApiError(error, 'تعذر فك ربط الجهاز') })
    } finally {
      setSaving(false)
    }
  }

  const openCreateDeviceDialog = (type: 'Main' | 'Sub' | 'Mobile' | 'MeraApp' | 'Api') => {
    setDeviceForm({
      ...emptyDeviceForm,
      type,
      meraApiUrl: defaultMeraApiUrlForType(type),
      mainDeviceId: type === 'Sub' || type === 'Mobile' || type === 'MeraApp' ? mainDevices[0]?.id || '' : '',
    })
    setDeviceDialogOpen(true)
  }

  const submitDevice = async () => {
    if (!selectedCustomerId) {
      setFeedback({ severity: 'error', message: 'اختر عميلاً أولاً' })
      return
    }

    if (!deviceForm.version.trim()) {
      setFeedback({ severity: 'error', message: 'الإصدار مطلوب' })
      return
    }

    if ((deviceForm.type === 'Sub' || deviceForm.type === 'Mobile' || deviceForm.type === 'MeraApp' || deviceForm.type === 'Api') && !deviceForm.mainDeviceId) {
      setFeedback({ severity: 'error', message: 'اختر الجهاز الرئيسي أولاً' })
      return
    }

    try {
      setSaving(true)
      const payload: CreateDevicePayload = {
        customerId: selectedCustomerId,
        type: deviceForm.type,
        version: deviceForm.version.trim(),
        deviceCode: deviceForm.deviceCode.trim() || undefined,
        mainDeviceId:
          deviceForm.type === 'Sub' || deviceForm.type === 'Mobile' || deviceForm.type === 'MeraApp' || deviceForm.type === 'Api'
            ? deviceForm.mainDeviceId
            : undefined,
        subscriptionType: deviceForm.subscriptionType,
        subscriptionDurationMonths: deviceForm.subscriptionType === 'Trial' ? deviceForm.subscriptionDurationMonths : undefined,
        renewalWarningDays: 3,
        gracePeriodDays: 3,
        notes: deviceForm.notes.trim() || undefined,
        meraApiUrl: (deviceForm.meraApiUrl.trim() || defaultMeraApiUrlForType(deviceForm.type)),
        useIndependentReportBranding:
          deviceForm.type === 'Mobile' || deviceForm.type === 'MeraApp'
            ? deviceForm.useIndependentReportBranding
            : undefined,
        createLinkedApi: deviceForm.type === 'Main' ? deviceForm.createLinkedApi : undefined,
        dbPasswordOverride:
          deviceForm.type === 'Main' || deviceForm.type === 'Api'
            ? deviceForm.dbPasswordOverride.trim() || undefined
            : undefined,
        apiHostingKind:
          deviceForm.type === 'Api' || (deviceForm.type === 'Main' && deviceForm.createLinkedApi)
            ? deviceForm.apiHostingKind
            : undefined,
      }

      const response = await deviceService.create(payload)
      const createdDevice = unwrapData<DeviceRecord>(response)
      setLatestCreatedDevice(createdDevice)
      const apiAct = createdDevice.linkedApiActivationCode
      setFeedback({
        severity: 'success',
        message: apiAct
          ? `تم إنشاء الرئيسي والـAPI.\nACT الرئيسي: ${createdDevice.activationCode}\nACT الـAPI: ${apiAct}${deviceForm.dbPasswordOverride.trim() ? '\nتم ضبط رمز الداتابيس للـAPI' : ''}`
          : `تم إنشاء الجهاز بنجاح. مفتاح التفعيل: ${createdDevice.activationCode}`,
      })
      setDeviceDialogOpen(false)
      setDeviceForm(emptyDeviceForm)
      await fetchCustomers()
      await loadCustomerDetails(selectedCustomerId, false)
      setDetailDialogOpen(true)
    } catch (error: any) {
      setFeedback({ severity: 'error', message: error?.response?.data?.message || 'تعذر إنشاء الجهاز' })
    } finally {
      setSaving(false)
    }
  }

  const downloadAgreement = async (customer: CustomerRecord | CustomerWithDevicesRecord) => {
    try {
      await customerService.downloadLicenseAgreement(customer.id, customer.name)
    } catch {
      setFeedback({ severity: 'error', message: 'تعذر تحميل اتفاقية الترخيص' })
    }
  }

  const uploadAgreementForCustomer = async (customerId: string, file: File) => {
    const validationError = validateAgreementFile(file)
    if (validationError) {
      setFeedback({ severity: 'error', message: validationError })
      return
    }

    try {
      setSaving(true)
      setAgreementUploadLabel(`جاري رفع ${file.name}...`)
      setAgreementUploadProgress(1)
      await customerService.uploadLicenseAgreement(customerId, file, (percent) => {
        setAgreementUploadProgress(percent)
      })
      setFeedback({ severity: 'success', message: 'تم رفع اتفاقية الترخيص بنجاح' })
      await fetchCustomers()
      if (selectedCustomerId === customerId) await loadCustomerDetails(customerId, false)
    } catch (error: unknown) {
      setFeedback({ severity: 'error', message: extractApiError(error, 'فشل رفع الاتفاقية') })
    } finally {
      setSaving(false)
      setAgreementUploadProgress(null)
      setAgreementUploadLabel('')
    }
  }

  return (
    <Box sx={{ maxWidth: 1400, mx: 'auto' }}>
      <Backdrop open={isUploadingAgreement} sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 2, flexDirection: 'column', gap: 2 }}>
        <CircularProgress color="inherit" size={56} />
        <Typography variant="h6">{agreementUploadLabel || 'جاري رفع اتفاقية الترخيص...'}</Typography>
        <Box sx={{ width: 'min(320px, 80vw)' }}>
          <LinearProgress variant="determinate" value={agreementUploadProgress ?? 0} sx={{ height: 10, borderRadius: 5 }} />
          <Typography align="center" sx={{ mt: 1 }}>{agreementUploadProgress ?? 0}%</Typography>
        </Box>
      </Backdrop>
      <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={2} sx={{ mb: 4 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 800, mb: 1 }}>
            العملاء والأجهزة
          </Typography>
          <Typography color="text.secondary">
            أضف العميل أولاً، وبعدها من تفاصيله أضف الأجهزة الرئيسية والفرعية.
          </Typography>
        </Box>

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
          <Button variant="outlined" startIcon={<Refresh />} onClick={fetchCustomers} disabled={loading}>
            تحديث
          </Button>
          <Button variant="contained" startIcon={<Add />} onClick={openCreateCustomerDialog}>
            إضافة عميل
          </Button>
        </Stack>
      </Stack>

      {feedback && (
        <Alert severity={feedback.severity} onClose={() => setFeedback(null)} sx={{ mb: 3, borderRadius: 2 }}>
          {feedback.message}
        </Alert>
      )}

      {isUploadingAgreement && (
        <Paper sx={{ p: 2, mb: 3, borderRadius: 2 }}>
          <Stack spacing={1}>
            <Typography variant="body2" color="text.secondary">{agreementUploadLabel || 'جاري رفع اتفاقية الترخيص...'}</Typography>
            <LinearProgress variant="determinate" value={agreementUploadProgress ?? 0} sx={{ height: 8, borderRadius: 4 }} />
            <Typography variant="caption" color="text.secondary" align="center">{agreementUploadProgress ?? 0}%</Typography>
          </Stack>
        </Paper>
      )}

      {latestCreatedDevice && (
        <Alert severity="success" sx={{ mb: 3, borderRadius: 2 }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ md: 'center' }}>
            <Typography sx={{ fontWeight: 700 }}>آخر جهاز تم إنشاؤه</Typography>
            <Chip icon={<VpnKey />} label={latestCreatedDevice.activationCode} color="success" />
            <Chip label={latestCreatedDevice.deviceCode} variant="outlined" />
          </Stack>
        </Alert>
      )}

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={4}>
          <Card sx={{ borderRadius: 3 }}><CardContent><Typography color="text.secondary">إجمالي العملاء</Typography><Typography variant="h4" sx={{ fontWeight: 800 }}>{customers.length}</Typography></CardContent></Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card sx={{ borderRadius: 3 }}><CardContent><Typography color="text.secondary">إجمالي الأجهزة</Typography><Typography variant="h4" sx={{ fontWeight: 800 }}>{customers.reduce((sum, customer) => sum + customer.deviceCount, 0)}</Typography></CardContent></Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card sx={{ borderRadius: 3 }}><CardContent><Typography color="text.secondary">بدون اتفاقية</Typography><Typography variant="h4" sx={{ fontWeight: 800, color: 'error.main' }}>{customers.filter((c) => !c.hasLicenseAgreement).length}</Typography></CardContent></Card>
        </Grid>
      </Grid>

      <Paper sx={{ p: 2.5, mb: 3, borderRadius: 3 }}>
        <TextField fullWidth label="بحث بالاسم أو الموقع أو الهاتف" value={searchTerm} onChange={(event) => setSearchTerm(event.target.value)} />
      </Paper>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>
      ) : filteredCustomers.length === 0 ? (
        <Paper sx={{ p: 6, textAlign: 'center', borderRadius: 4 }}>
          <Avatar sx={{ mx: 'auto', mb: 2, width: 64, height: 64, bgcolor: 'primary.main' }}><Apartment /></Avatar>
          <Typography variant="h5" sx={{ fontWeight: 800, mb: 1 }}>النظام حالياً فارغ</Typography>
          <Typography color="text.secondary" sx={{ mb: 3 }}>أضف أول عميل، وبعدها من داخل التفاصيل أضف الأجهزة وسيتم توليد مفتاح التفعيل تلقائياً.</Typography>
          <Button variant="contained" startIcon={<Add />} onClick={openCreateCustomerDialog}>إضافة أول عميل</Button>
        </Paper>
      ) : (
        <Grid container spacing={3}>
          {filteredCustomers.map((customer) => (
            <Grid item xs={12} md={6} lg={4} key={customer.id}>
              <Card sx={{
                height: '100%',
                borderRadius: 4,
                boxShadow: '0 14px 30px rgba(15,23,42,0.08)',
                border: customer.hasLicenseAgreement ? undefined : '2px solid',
                borderColor: customer.hasLicenseAgreement ? undefined : 'error.main',
              }}>
                <CardContent sx={{ p: 3 }}>
                  <Stack direction="row" justifyContent="space-between" spacing={2} sx={{ mb: 2 }}>
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <Avatar sx={{ bgcolor: customer.hasLicenseAgreement ? 'primary.main' : 'error.main' }}>{customer.name.charAt(0)}</Avatar>
                      <Box>
                        <Typography variant="h6" sx={{ fontWeight: 800 }}>{customer.name}</Typography>
                        <Typography color="text.secondary" variant="body2">{customer.location}</Typography>
                      </Box>
                    </Stack>
                    <Stack spacing={0.5} alignItems="flex-end">
                      <Chip label={`${customer.deviceCount} جهاز`} color={customer.deviceCount > 0 ? 'primary' : 'default'} size="small" />
                      {!customer.hasLicenseAgreement && (
                        <Chip icon={<WarningAmber />} label="بدون اتفاقية" color="error" size="small" variant="outlined" />
                      )}
                      <Chip
                        label={customer.enableReps !== false ? 'المندوبين: مفعّل' : 'المندوبين: متوقف'}
                        color={customer.enableReps !== false ? 'success' : 'default'}
                        size="small"
                        variant={customer.enableReps !== false ? 'filled' : 'outlined'}
                      />
                      <Chip
                        label={customer.enableInstallments !== false ? 'الأقساط: مفعّل' : 'الأقساط: متوقف'}
                        color={customer.enableInstallments !== false ? 'success' : 'default'}
                        size="small"
                        variant={customer.enableInstallments !== false ? 'filled' : 'outlined'}
                      />
                    </Stack>
                  </Stack>

                  <Stack spacing={1} sx={{ mb: 2 }}>
                    <Stack direction="row" spacing={1} alignItems="center"><Phone fontSize="small" color="action" /><Typography variant="body2">{customer.phone || 'بدون رقم هاتف'}</Typography></Stack>
                    <Stack direction="row" spacing={1} alignItems="center"><Devices fontSize="small" color="action" /><Typography variant="body2">أضيف بواسطة: {customer.createdByName}</Typography></Stack>
                    <Typography variant="body2" color="text.secondary">تاريخ الإنشاء: {formatDate(customer.createdAt)}</Typography>
                  </Stack>

                  <Typography variant="body2" color="text.secondary" sx={{ minHeight: 44, mb: 2 }}>
                    {customer.notes || 'لا توجد ملاحظات لهذا العميل.'}
                  </Typography>

                  <Stack direction="row" spacing={1}>
                    <Button fullWidth variant="contained" startIcon={<Visibility />} onClick={() => loadCustomerDetails(customer.id)}>التفاصيل</Button>
                    <IconButton color="primary" onClick={() => openEditCustomerDialog(customer)}><Edit /></IconButton>
                    <IconButton
                      color="error"
                      onClick={() => deleteCustomer(customer)}
                      disabled={customer.deviceCount > 0}
                      title={customer.deviceCount > 0 ? 'احذف الأجهزة غير المرتبطة أولاً' : 'حذف العميل'}
                    >
                      <Delete />
                    </IconButton>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      <Dialog open={customerDialogOpen} onClose={() => setCustomerDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{editingCustomerId ? 'تعديل العميل' : 'إضافة عميل جديد'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField label="اسم العميل" value={customerForm.name} onChange={(event) => setCustomerForm((prev) => ({ ...prev, name: event.target.value }))} required fullWidth />
            <TextField label="الموقع" value={customerForm.location} onChange={(event) => setCustomerForm((prev) => ({ ...prev, location: event.target.value }))} required fullWidth />
            <TextField label="رقم الهاتف" value={customerForm.phone} onChange={(event) => setCustomerForm((prev) => ({ ...prev, phone: event.target.value }))} fullWidth />
            <TextField label="ملاحظات" value={customerForm.notes} onChange={(event) => setCustomerForm((prev) => ({ ...prev, notes: event.target.value }))} multiline minRows={3} fullWidth />
            <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>وحدات نظام ميرا</Typography>
            <Typography variant="caption" color="text.secondary">
              إن أُوقفت تبقى الأزرار ظاهرة في ميرا لكنها تكون معطّلة. الحسابات الحالية تبقى مفعّلة ما لم تُوقفها هنا.
            </Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={customerForm.enableReps}
                  onChange={(event) => setCustomerForm((prev) => ({ ...prev, enableReps: event.target.checked }))}
                />
              }
              label="المندوبين"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={customerForm.enableInstallments}
                  onChange={(event) => setCustomerForm((prev) => ({ ...prev, enableInstallments: event.target.checked }))}
                />
              }
              label="الأقساط"
            />
            <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>بيانات التقارير (تُزامَن مع ميرا)</Typography>
            <TextField label="اسم الشركة / صاحب العمل" value={customerForm.ownerName} onChange={(e) => setCustomerForm((p) => ({ ...p, ownerName: e.target.value }))} fullWidth />
            <TextField label="تخصص العمل" value={customerForm.specialization} onChange={(e) => setCustomerForm((p) => ({ ...p, specialization: e.target.value }))} fullWidth />
            <Typography variant="caption" color="text.secondary">
              رفع الصور واختيار ارتفاع الترويسة من صفحة تفاصيل العميل — مع معاينة مباشرة.
            </Typography>
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>اتفاقية وشروط الاستخدام الموقّعة (PDF)</Typography>
              <Stack direction="row" spacing={1} alignItems="center">
                <Button variant="outlined" startIcon={<UploadFile />} onClick={() => agreementInputRef.current?.click()} disabled={isUploadingAgreement}>
                  {agreementFile ? agreementFile.name : 'اختر ملف PDF'}
                </Button>
                {agreementFile && (
                  <Button size="small" onClick={() => setAgreementFile(null)}>إزالة</Button>
                )}
              </Stack>
              {!agreementFile && !editingCustomerId && (
                <Typography variant="caption" color="error.main" sx={{ mt: 1, display: 'block' }}>
                  إن لم تُرفع الاتفاقية سيظهر العميل باللون الأحمر حتى ترفعها لاحقاً.
                </Typography>
              )}
              {isUploadingAgreement && (
                <Box sx={{ mt: 2 }}>
                  <LinearProgress variant="determinate" value={agreementUploadProgress ?? 0} sx={{ mb: 0.5, height: 6, borderRadius: 3 }} />
                  <Typography variant="caption" color="text.secondary">{agreementUploadProgress ?? 0}% — {agreementUploadLabel}</Typography>
                </Box>
              )}
              <input
                ref={agreementInputRef}
                type="file"
                accept=".pdf,application/pdf"
                hidden
                onChange={(e) => {
                  const file = e.target.files?.[0] ?? null
                  if (file) {
                    const err = validateAgreementFile(file)
                    if (err) {
                      setFeedback({ severity: 'error', message: err })
                      e.target.value = ''
                      return
                    }
                  }
                  setAgreementFile(file)
                }}
              />
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2.5 }}>
          <Button onClick={() => setCustomerDialogOpen(false)} disabled={isUploadingAgreement}>إلغاء</Button>
          <Button variant="contained" onClick={submitCustomer} disabled={saving || isUploadingAgreement}>
            {isUploadingAgreement ? `جاري الرفع ${agreementUploadProgress ?? 0}%` : saving ? 'جاري الحفظ...' : editingCustomerId ? 'حفظ' : 'إنشاء'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={detailDialogOpen} onClose={() => setDetailDialogOpen(false)} fullWidth maxWidth="lg">
        <DialogTitle>تفاصيل العميل والأجهزة</DialogTitle>
        <DialogContent dividers>
          {detailLoading || !selectedCustomer ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
          ) : (
            <Stack spacing={3}>
              <Paper sx={{ p: 3, borderRadius: 3, bgcolor: 'grey.50' }}>
                <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={2}>
                  <Box>
                    <Typography variant="h5" sx={{ fontWeight: 800, mb: 1 }}>{selectedCustomer.name}</Typography>
                    <Typography color="text.secondary">{selectedCustomer.location}</Typography>
                    <Typography color="text.secondary">{selectedCustomer.phone || 'بدون رقم هاتف'}</Typography>
                    {(selectedCustomer.ownerName || selectedCustomer.specialization) && (
                      <Typography sx={{ mt: 1, fontWeight: 600 }}>
                        {[selectedCustomer.ownerName, selectedCustomer.specialization].filter(Boolean).join(' — ')}
                      </Typography>
                    )}
                    {selectedCustomer.routeSiteCode ? (
                      <Typography color="primary.main" sx={{ fontWeight: 700, mt: 1 }}>
                        رمز Mera Route: {selectedCustomer.routeSiteCode}
                      </Typography>
                    ) : (
                      <Typography color="warning.main" sx={{ mt: 1 }}>
                        رمز Mera Route: غير مُولَّد بعد — يظهر بعد تفعيل الجهاز الرئيسي
                      </Typography>
                    )}
                    <Stack direction="row" spacing={1} sx={{ mt: 1.5 }} flexWrap="wrap" useFlexGap>
                      <Chip
                        size="small"
                        label={selectedCustomer.enableReps !== false ? 'المندوبين: مفعّل' : 'المندوبين: متوقف'}
                        color={selectedCustomer.enableReps !== false ? 'success' : 'default'}
                      />
                      <Chip
                        size="small"
                        label={selectedCustomer.enableInstallments !== false ? 'الأقساط: مفعّل' : 'الأقساط: متوقف'}
                        color={selectedCustomer.enableInstallments !== false ? 'success' : 'default'}
                      />
                    </Stack>
                  </Box>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                    <Button
                      variant={selectedCustomer.hasLicenseAgreement ? 'outlined' : 'contained'}
                      color={selectedCustomer.hasLicenseAgreement ? 'primary' : 'error'}
                      startIcon={<Description />}
                      onClick={() => selectedCustomer.hasLicenseAgreement && downloadAgreement(selectedCustomer)}
                      disabled={!selectedCustomer.hasLicenseAgreement}
                    >
                      {selectedCustomer.hasLicenseAgreement ? 'اتفاقية الترخيص' : 'لا توجد اتفاقية'}
                    </Button>
                    <Button variant="outlined" startIcon={<UploadFile />} component="label" disabled={isUploadingAgreement || saving}>
                      {isUploadingAgreement ? `جاري الرفع ${agreementUploadProgress ?? 0}%` : selectedCustomer.hasLicenseAgreement ? 'استبدال الاتفاقية' : 'رفع الاتفاقية'}
                      <input
                        type="file"
                        accept=".pdf,application/pdf"
                        hidden
                        disabled={isUploadingAgreement || saving}
                        onChange={(e) => {
                          const file = e.target.files?.[0]
                          if (file && selectedCustomerId) uploadAgreementForCustomer(selectedCustomerId, file)
                          e.target.value = ''
                        }}
                      />
                    </Button>
                    <Button variant="contained" startIcon={<Add />} onClick={() => openCreateDeviceDialog('Main')}>إضافة جهاز رئيسي</Button>
                    <Button variant="outlined" startIcon={<Add />} onClick={() => openCreateDeviceDialog('Sub')} disabled={mainDevices.length === 0}>إضافة جهاز فرعي</Button>
                    <Button variant="outlined" startIcon={<Add />} onClick={() => openCreateDeviceDialog('Mobile')} disabled={mainDevices.length === 0}>إضافة موبايل Mera Route</Button>
                    <Button variant="outlined" startIcon={<Add />} onClick={() => openCreateDeviceDialog('MeraApp')} disabled={mainDevices.length === 0}>إضافة Mera موبايل</Button>
                    <Button variant="outlined" color="secondary" startIcon={<Add />} onClick={() => openCreateDeviceDialog('Api')}>إضافة API</Button>
                    {selectedCustomer.devices.length === 0 && selectedCustomerId && (
                      <Button color="error" variant="outlined" startIcon={<Delete />} onClick={() => deleteCustomer(selectedCustomer)}>
                        حذف العميل
                      </Button>
                    )}
                  </Stack>
                </Stack>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mt: 2 }}>
                  <Chip label={`الإجمالي: ${selectedCustomer.devices.length}`} color="primary" />
                  <Chip label={`رئيسية: ${mainDevices.length}`} variant="outlined" />
                  <Chip label={`فرعية: ${subDevices.length}`} variant="outlined" />
                  <Chip label={`موبايل Route: ${mobileDevices.length}`} variant="outlined" />
                  <Chip label={`Mera موبايل: ${meraAppDevices.length}`} variant="outlined" />
                  <Chip label={`API: ${apiDevices.length}`} variant="outlined" color="secondary" />
                </Stack>
                <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, fontWeight: 800 }}>صور التقارير — معاينة مثل ميرا</Typography>
                <ReportBrandingPanel
                  headerUrl={selectedCustomer.reportHeaderImageUrl}
                  footerUrl={selectedCustomer.reportFooterImageUrl}
                  receiptUrl={selectedCustomer.receiptHeaderImageUrl}
                  headerMm={selectedCustomer.reportHeaderHeightMm ?? 35}
                  footerMm={selectedCustomer.reportFooterHeightMm ?? 25}
                  receiptMm={selectedCustomer.receiptHeaderHeightMm ?? 30}
                  cacheKey={selectedCustomer.reportBrandingUpdatedAt}
                  disabled={saving}
                  onUpload={async (kind, file) => {
                    if (!selectedCustomerId) return
                    setSaving(true)
                    try {
                      await customerReportBrandingService.uploadImage(selectedCustomerId, kind, file)
                      setFeedback({ severity: 'success', message: 'تم رفع الصورة — القديمة استُبدلت' })
                      await loadCustomerDetails(selectedCustomerId, false)
                    } catch (error: unknown) {
                      setFeedback({ severity: 'error', message: extractApiError(error, 'تعذر رفع الصورة') })
                      throw error
                    } finally {
                      setSaving(false)
                    }
                  }}
                  onDelete={async (kind) => {
                    if (!selectedCustomerId) return
                    setSaving(true)
                    try {
                      await customerReportBrandingService.deleteImage(selectedCustomerId, kind)
                      setFeedback({ severity: 'success', message: 'تم حذف الصورة' })
                      await loadCustomerDetails(selectedCustomerId, false)
                    } catch (error: unknown) {
                      setFeedback({ severity: 'error', message: extractApiError(error, 'تعذر حذف الصورة') })
                    } finally {
                      setSaving(false)
                    }
                  }}
                  onHeightChange={async (kind, mm) => {
                    if (!selectedCustomerId || !selectedCustomer) return
                    const next = {
                      ownerName: selectedCustomer.ownerName ?? undefined,
                      specialization: selectedCustomer.specialization ?? undefined,
                      reportHeaderHeightMm: kind === 'header' ? mm : selectedCustomer.reportHeaderHeightMm ?? 35,
                      reportFooterHeightMm: kind === 'footer' ? mm : selectedCustomer.reportFooterHeightMm ?? 25,
                      receiptHeaderHeightMm: kind === 'receiptHeader' ? mm : selectedCustomer.receiptHeaderHeightMm ?? 30,
                    }
                    setSaving(true)
                    try {
                      await customerReportBrandingService.update(selectedCustomerId, next)
                      await loadCustomerDetails(selectedCustomerId, false)
                    } catch (error: unknown) {
                      setFeedback({ severity: 'error', message: extractApiError(error, 'تعذر حفظ الارتفاع') })
                    } finally {
                      setSaving(false)
                    }
                  }}
                />
              </Paper>

              {selectedCustomer.devices.length === 0 ? (
                <Paper sx={{ p: 5, textAlign: 'center', borderRadius: 3 }}>
                  <Typography variant="h6" sx={{ fontWeight: 800, mb: 1 }}>لا توجد أجهزة لهذا العميل بعد</Typography>
                  <Typography color="text.secondary" sx={{ mb: 3 }}>أضف جهازاً رئيسياً أولاً، ثم ستظهر لك إمكانية إضافة الأجهزة الفرعية تحته.</Typography>
                  <Button variant="contained" startIcon={<Add />} onClick={() => openCreateDeviceDialog('Main')}>إضافة جهاز رئيسي</Button>
                </Paper>
              ) : (
                <Stack spacing={2}>
                  {apiDevices.length > 0 && (
                    <Paper sx={{ p: 2, borderRadius: 3, border: '1px dashed', borderColor: 'secondary.main' }}>
                      <Typography sx={{ fontWeight: 800, mb: 1.5 }}>أجهزة API</Typography>
                      <Grid container spacing={2}>
                        {apiDevices.map((device) => (
                          <Grid item xs={12} md={6} key={device.id}>
                            <Paper sx={{ p: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                                <Typography sx={{ fontWeight: 700 }}>{getDeviceTypeLabel(device.type)}</Typography>
                                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                  <Chip
                                    size="small"
                                    label={getStatusLabel(device.status)}
                                    color={device.status === 'Active' ? 'success' : device.status === 'Suspended' ? 'error' : 'warning'}
                                  />
                                  <Button size="small" variant="contained" startIcon={<Edit />} onClick={() => setEditingDevice(device)}>
                                    تعديل
                                  </Button>
                                  {canResetDeviceBinding(device) && selectedCustomerId && (
                                    <Button
                                      size="small"
                                      color="warning"
                                      variant="outlined"
                                      startIcon={<RestartAlt />}
                                      disabled={saving}
                                      onClick={() => resetDeviceBinding(device, selectedCustomerId)}
                                    >
                                      فك الربط
                                    </Button>
                                  )}
                                  {isUnlinkedDevice(device) && selectedCustomerId && (
                                    <Button
                                      size="small"
                                      color="error"
                                      variant="outlined"
                                      startIcon={<Delete />}
                                      disabled={saving}
                                      onClick={() => deleteUnlinkedDevice(device, selectedCustomerId)}
                                    >
                                      حذف
                                    </Button>
                                  )}
                                </Stack>
                              </Stack>
                              <Typography variant="body2" sx={{ fontWeight: 800 }}>
                                رقم الرئيسية: {device.mainComputerName
                                  || mainDevices.find((m) => m.id === device.mainDeviceId)?.computerName
                                  || '—'}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                تفعيل الرئيسية: {device.mainActivationCode
                                  || mainDevices.find((m) => m.id === device.mainDeviceId)?.activationCode
                                  || '—'}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">تفعيل API: {device.activationCode}</Typography>
                              <Typography variant="body2" color="text.secondary" dir="ltr">Device Code: {device.deviceCode}</Typography>
                              <Typography variant="body2" color="text.secondary">
                                الاستضافة: {device.apiHostingKind === 'Host' ? 'هوست (غيّرته يدوياً)' : 'ويندوز (الافتراضي)'}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">الإصدار: {device.version}</Typography>
                              <Typography variant="body2" color="text.secondary">
                                Mera API: {device.meraApiUrl?.trim() || '—'}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                باسوورد الداتابيس: {device.hasDbPasswordOverride ? 'مخصّص' : 'افتراضي الموقع'}
                              </Typography>
                              {device.updatePending && <Chip size="small" color="warning" label="بانتظار التحديث" sx={{ mt: 1 }} />}
                              <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                                <TextField
                                  size="small"
                                  type="password"
                                  label="تغيير باسوورد الداتابيس"
                                  id={`api-db-pwd-${device.id}`}
                                  fullWidth
                                />
                                <Button
                                  size="small"
                                  variant="outlined"
                                  disabled={saving}
                                  onClick={async () => {
                                    const el = document.getElementById(`api-db-pwd-${device.id}`) as HTMLInputElement | null
                                    if (!selectedCustomerId || !el) return
                                    try {
                                      setSaving(true)
                                      await deviceService.updateSiteSettings(device.id, {
                                        dbPasswordOverride: el.value.trim() || null,
                                      })
                                      setFeedback({ severity: 'success', message: 'تم حفظ باسوورد الداتابيس' })
                                      await loadCustomerDetails(selectedCustomerId, false)
                                      el.value = ''
                                    } catch (error: unknown) {
                                      setFeedback({ severity: 'error', message: extractApiError(error, 'تعذر الحفظ') })
                                    } finally {
                                      setSaving(false)
                                    }
                                  }}
                                >
                                  حفظ
                                </Button>
                              </Stack>
                              <Alert severity={device.lastUpdatedAt ? 'success' : 'warning'} sx={{ mt: 1.5, borderRadius: 2, py: 0.5 }}>
                                <Typography variant="caption" sx={{ fontWeight: 800, display: 'block' }}>آخر تحديث (تاريخ ووقت)</Typography>
                                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                  {device.lastUpdatedAt ? formatDate(device.lastUpdatedAt) : 'لا يوجد تحديث مسجّل'}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" display="block">
                                  بواسطة: {device.lastUpdatedByName || '—'}
                                  {device.lastUpdatedVersion ? ` · v${device.lastUpdatedVersion}` : ''}
                                </Typography>
                                {(device.recentUpdateLogs?.length ?? 0) > 0 &&
                                  device.recentUpdateLogs!.slice(0, 5).map((log) => (
                                    <Typography key={log.id} variant="caption" display="block" color="text.secondary">
                                      {formatDate(log.updatedAt)} — {log.updatedByName || '—'}
                                      {log.fromVersion || log.toVersion
                                        ? ` (${log.fromVersion || '?'} → ${log.toVersion || '?'})`
                                        : ''}
                                    </Typography>
                                  ))}
                              </Alert>
                            </Paper>
                          </Grid>
                        ))}
                      </Grid>
                    </Paper>
                  )}

                  {mainDevices.map((mainDevice) => {
                    const children = selectedCustomer.devices.filter(
                      (device) => device.mainDeviceId === mainDevice.id && device.type !== 'Main',
                    )
                    return (
                      <Card key={mainDevice.id} sx={{ borderRadius: 3 }}>
                        <CardContent>
                          <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={2} sx={{ mb: 2 }}>
                            <Box>
                              <Typography variant="h6" sx={{ fontWeight: 800, mb: 1 }}>جهاز رئيسي</Typography>
                              <Typography variant="body2" sx={{ fontWeight: 800 }}>رقم الجهاز: {mainDevice.computerName || '—'}</Typography>
                              <Typography variant="body2" color="text.secondary">تفعيل الرئيسية: {mainDevice.activationCode}</Typography>
                              <Typography variant="body2" color="text.secondary">Device Code: {mainDevice.deviceCode}</Typography>
                            </Box>
                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
                              <Chip label={getStatusLabel(mainDevice.status)} color={mainDevice.status === 'Active' ? 'success' : mainDevice.status === 'Suspended' ? 'error' : 'warning'} />
                              <Chip label={`الإصدار ${mainDevice.version}`} variant="outlined" />
                              {mainDevice.lastUpdatedAt && (
                                <Chip
                                  size="small"
                                  color="success"
                                  variant="outlined"
                                  label={`آخر تحديث: ${formatDate(mainDevice.lastUpdatedAt)}`}
                                />
                              )}
                              <Chip label={`${children.length} تابع`} variant="outlined" />
                              <Chip
                                label={getSubscriptionLabel(mainDevice.subscriptionType, mainDevice.subscriptionDurationMonths)}
                                color="secondary"
                                variant="outlined"
                              />
                              <Chip
                                label={getSubscriptionStateLabel(mainDevice.subscriptionState)}
                                color={getSubscriptionStateColor(mainDevice.subscriptionState) as any}
                              />
                              <Button size="small" variant="contained" startIcon={<Edit />} onClick={() => setEditingDevice(mainDevice)}>
                                تعديل
                              </Button>
                              {isUnlinkedDevice(mainDevice) && selectedCustomerId && (
                                <Button size="small" color="error" variant="outlined" startIcon={<Delete />} onClick={() => deleteUnlinkedDevice(mainDevice, selectedCustomerId)}>
                                  حذف
                                </Button>
                              )}
                            </Stack>
                          </Stack>

                          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            تاريخ التفعيل: {formatDate(mainDevice.activatedAt)}
                          </Typography>
                          <Alert severity={mainDevice.lastUpdatedAt ? 'success' : 'warning'} sx={{ mb: 1.5, borderRadius: 2 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 0.5 }}>آخر تحديث للنظام</Typography>
                            <Typography variant="body2">
                              التاريخ/الوقت: {mainDevice.lastUpdatedAt ? formatDate(mainDevice.lastUpdatedAt) : 'لا يوجد تحديث مسجّل بعد'}
                            </Typography>
                            <Typography variant="body2">
                              بواسطة: {mainDevice.lastUpdatedByName || '—'}
                              {mainDevice.lastUpdatedVersion ? ` · الإصدار ${mainDevice.lastUpdatedVersion}` : ''}
                              {mainDevice.lastUpdateProduct ? ` · ${mainDevice.lastUpdateProduct}` : ''}
                            </Typography>
                            {(mainDevice.recentUpdateLogs?.length ?? 0) > 0 && (
                              <Box sx={{ mt: 1 }}>
                                <Typography variant="caption" sx={{ fontWeight: 800, display: 'block', mb: 0.5 }}>سجل التحديثات</Typography>
                                {mainDevice.recentUpdateLogs!.slice(0, 5).map((log) => (
                                  <Typography key={log.id} variant="caption" display="block" color="text.secondary">
                                    {formatDate(log.updatedAt)} — {log.updatedByName || '—'}
                                    {log.fromVersion || log.toVersion ? ` (${log.fromVersion || '?'} → ${log.toVersion || '?'})` : ''}
                                  </Typography>
                                ))}
                              </Box>
                            )}
                          </Alert>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                            نهاية الاشتراك: {formatDate(mainDevice.subscriptionEndsAt)}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            {mainDevice.subscriptionAlertMessage}
                          </Typography>
                          <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 0.5 }}>تفاصيل التفعيل</Typography>
                            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                              {mainDevice.notes?.trim() || 'لا توجد تفاصيل مسجّلة لهذا الجهاز.'}
                            </Typography>
                          </Alert>
                          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 2 }} alignItems={{ sm: 'center' }}>
                            <TextField
                              size="small"
                              fullWidth
                              label="عنوان Mera API (الحاسبة تأخذه من الموقع)"
                              placeholder={DEFAULT_MERA_API_URL_DESKTOP}
                              helperText="مثال: http://localhost:5078 — ليس 0.0.0.0"
                              defaultValue={mainDevice.meraApiUrl || DEFAULT_MERA_API_URL_DESKTOP}
                              key={`mera-url-${mainDevice.id}-${mainDevice.meraApiUrl || ''}`}
                              id={`mera-api-url-${mainDevice.id}`}
                            />
                            <Button
                              size="small"
                              variant="contained"
                              disabled={saving}
                              onClick={async () => {
                                const el = document.getElementById(`mera-api-url-${mainDevice.id}`) as HTMLInputElement | null
                                if (!selectedCustomerId || !el) return
                                try {
                                  setSaving(true)
                                  await deviceService.updateSiteSettings(mainDevice.id, {
                                    meraApiUrl: el.value.trim() || null,
                                  })
                                  setFeedback({ severity: 'success', message: 'تم حفظ عنوان Mera API' })
                                  await loadCustomerDetails(selectedCustomerId, false)
                                } catch (error: unknown) {
                                  setFeedback({ severity: 'error', message: extractApiError(error, 'تعذر حفظ العنوان') })
                                } finally {
                                  setSaving(false)
                                }
                              }}
                            >
                              حفظ الإيبي
                            </Button>
                          </Stack>
                          {(() => {
                            const linkedApi =
                              apiDevices.find((a) => a.mainDeviceId === mainDevice.id)
                              || (apiDevices.length === 1 && !apiDevices[0].mainDeviceId ? apiDevices[0] : undefined)
                            const unlinkedApis = apiDevices.filter((a) => !a.mainDeviceId && a.id !== linkedApi?.id)
                            return (
                              <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 0.5 }}>حزمة الرئيسي + API</Typography>
                                {linkedApi ? (
                                  <>
                                    <Typography variant="body2">رقم الرئيسية: <b>{mainDevice.computerName || '—'}</b></Typography>
                                    <Typography variant="body2">تفعيل الرئيسية: <b>{mainDevice.activationCode}</b></Typography>
                                    <Typography variant="body2">تفعيل API: <b>{linkedApi.activationCode}</b></Typography>
                                    <Typography variant="body2" color="text.secondary">
                                      الاستضافة: {linkedApi.apiHostingKind === 'Host' ? 'هوست (غيّرته يدوياً — تحديث يدوي)' : 'ويندوز على الرئيسية (الافتراضي)'}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                      باسوورد الداتابيس: {linkedApi.hasDbPasswordOverride ? 'مخصّص للجهاز' : 'الافتراضي من إعدادات الموقع'}
                                    </Typography>
                                    {!linkedApi.mainDeviceId && (
                                      <Button
                                        size="small"
                                        sx={{ mt: 1 }}
                                        variant="outlined"
                                        disabled={saving}
                                        onClick={async () => {
                                          if (!selectedCustomerId) return
                                          try {
                                            setSaving(true)
                                            await deviceService.update(linkedApi.id, {
                                              customerId: selectedCustomerId,
                                              type: 'Api',
                                              mainDeviceId: mainDevice.id,
                                              status: linkedApi.status,
                                              version: linkedApi.version,
                                              subscriptionType: linkedApi.subscriptionType,
                                              subscriptionDurationMonths: linkedApi.subscriptionDurationMonths ?? undefined,
                                              renewalWarningDays: 3,
                                              gracePeriodDays: 3,
                                              notes: linkedApi.notes,
                                              meraApiUrl: linkedApi.meraApiUrl,
                                              apiHostingKind: linkedApi.apiHostingKind === 'Host' ? 'Host' : 'Windows',
                                            })
                                            setFeedback({ severity: 'success', message: 'تم ربط الـ API بهذه الرئيسية' })
                                            await loadCustomerDetails(selectedCustomerId, false)
                                          } catch (error: unknown) {
                                            setFeedback({ severity: 'error', message: extractApiError(error, 'تعذر ربط الـ API') })
                                          } finally {
                                            setSaving(false)
                                          }
                                        }}
                                      >
                                        ربط هذا الـ API بهذه الرئيسية
                                      </Button>
                                    )}
                                  </>
                                ) : (
                                  <Typography variant="body2">لا يوجد API مربوط بهذه الرئيسية — أنشئه مع الرئيسي أو اربطه من جهاز API موجود.</Typography>
                                )}
                                {unlinkedApis.length > 0 && (
                                  <TextField
                                    select
                                    size="small"
                                    label="ربط API موجود"
                                    value=""
                                    sx={{ mt: 1.5, minWidth: 240 }}
                                    onChange={async (event) => {
                                      const apiId = event.target.value
                                      if (!selectedCustomerId || !apiId) return
                                      const api = unlinkedApis.find((a) => a.id === apiId)
                                      if (!api) return
                                      try {
                                        setSaving(true)
                                        await deviceService.update(api.id, {
                                          customerId: selectedCustomerId,
                                          type: 'Api',
                                          mainDeviceId: mainDevice.id,
                                          status: api.status,
                                          version: api.version,
                                          subscriptionType: api.subscriptionType,
                                          subscriptionDurationMonths: api.subscriptionDurationMonths ?? undefined,
                                          renewalWarningDays: 3,
                                          gracePeriodDays: 3,
                                          notes: api.notes,
                                          meraApiUrl: api.meraApiUrl,
                                          apiHostingKind: api.apiHostingKind === 'Host' ? 'Host' : 'Windows',
                                        })
                                        setFeedback({ severity: 'success', message: 'تم ربط الـ API بهذه الرئيسية' })
                                        await loadCustomerDetails(selectedCustomerId, false)
                                      } catch (error: unknown) {
                                        setFeedback({ severity: 'error', message: extractApiError(error, 'تعذر ربط الـ API') })
                                      } finally {
                                        setSaving(false)
                                      }
                                    }}
                                  >
                                    {unlinkedApis.map((api) => (
                                      <MenuItem key={api.id} value={api.id}>{api.activationCode}</MenuItem>
                                    ))}
                                  </TextField>
                                )}
                                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mt: 1.5 }} alignItems={{ sm: 'center' }}>
                                  <FormControlLabel
                                    control={
                                      <Switch
                                        defaultChecked={linkedApi?.apiHostingKind !== 'Host'}
                                        disabled={linkedApi?.apiHostingKind === 'Host'}
                                        id={`upd-api-with-main-${mainDevice.id}`}
                                        size="small"
                                      />
                                    }
                                    label={linkedApi?.apiHostingKind === 'Host' ? 'الـAPI هوست — لا يتحدث مع الرئيسية' : 'تحديث الـAPI معه'}
                                  />
                                  <Button
                                    size="small"
                                    variant="contained"
                                    color="warning"
                                    disabled={saving}
                                    onClick={async () => {
                                      if (!selectedCustomerId) return
                                      const sw = document.getElementById(`upd-api-with-main-${mainDevice.id}`) as HTMLInputElement | null
                                      const withApi = sw?.checked !== false
                                      if (!window.confirm(
                                        withApi
                                          ? 'تعليمليم تحديث الرئيسي والـAPI معاً؟ عند فتح النظام: يحدَّث الـAPI أولاً ثم الرئيسي.'
                                          : 'تعليمليم تحديث الجهاز الرئيسي فقط؟',
                                      )) return
                                      try {
                                        setSaving(true)
                                        await deviceService.setUpdatePending(mainDevice.id, true, withApi)
                                        setFeedback({
                                          severity: 'success',
                                          message: withApi
                                            ? 'تم تعليم الرئيسي والـAPI للتحديث'
                                            : 'تم تعليم الرئيسي للتحديث',
                                        })
                                        await loadCustomerDetails(selectedCustomerId, false)
                                      } catch (error: unknown) {
                                        setFeedback({ severity: 'error', message: extractApiError(error, 'تعذر تعليم التحديث') })
                                      } finally {
                                        setSaving(false)
                                      }
                                    }}
                                  >
                                    {mainDevice.updatePending ? 'معاد تعليم التحديث' : 'تعليمليم تحديث الآن'}
                                  </Button>
                                  {mainDevice.updatePending && (
                                    <Chip size="small" color="warning" label="بانتظار التحديث" />
                                  )}
                                </Stack>
                              </Alert>
                            )
                          })()}

                          {children.length === 0 ? (
                            <Alert severity="info">لا توجد أجهزة فرعية مرتبطة بهذا الجهاز الرئيسي.</Alert>
                          ) : (
                            <Grid container spacing={2}>
                              {children.map((device) => (
                                <Grid item xs={12} md={6} key={device.id}>
                                  <Paper sx={{ p: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                                      <Typography sx={{ fontWeight: 700 }}>
                                        {getDeviceTypeLabel(device.type)}
                                      </Typography>
                                      <Button size="small" variant="contained" startIcon={<Edit />} onClick={() => setEditingDevice(device)}>
                                        تعديل
                                      </Button>
                                    </Stack>
                                    <Typography variant="body2" color="text.secondary">Device Code: {device.deviceCode}</Typography>
                                    <Typography variant="body2" color="text.secondary">Activation Key: {device.activationCode}</Typography>
                                    <Typography variant="body2" color="text.secondary">الإصدار: {device.version}</Typography>
                                    {(device.type === 'Mobile' || device.type === 'MeraApp' || device.type === 'Sub') && selectedCustomerId && (
                                      <Stack direction="row" spacing={1} sx={{ mt: 1.5 }} alignItems="flex-start">
                                        <TextField
                                          size="small"
                                          fullWidth
                                          label="عنوان Mera API"
                                          placeholder={
                                            device.type === 'Mobile' || device.type === 'MeraApp'
                                              ? DEFAULT_MERA_API_URL_MOBILE
                                              : DEFAULT_MERA_API_URL_DESKTOP
                                          }
                                          helperText={
                                            device.type === 'Mobile' || device.type === 'MeraApp'
                                              ? 'IP الشبكة المحلية — ليس localhost'
                                              : undefined
                                          }
                                          defaultValue={
                                            device.meraApiUrl
                                            || (device.type === 'Mobile' || device.type === 'MeraApp'
                                              ? DEFAULT_MERA_API_URL_MOBILE
                                              : DEFAULT_MERA_API_URL_DESKTOP)
                                          }
                                          key={`mera-url-${device.id}-${device.meraApiUrl || ''}`}
                                          id={`mera-api-url-${device.id}`}
                                        />
                                        <Button
                                          size="small"
                                          variant="contained"
                                          disabled={saving}
                                          sx={{ mt: 0.5, flexShrink: 0 }}
                                          onClick={async () => {
                                            const el = document.getElementById(`mera-api-url-${device.id}`) as HTMLInputElement | null
                                            if (!el) return
                                            try {
                                              setSaving(true)
                                              await deviceService.updateSiteSettings(device.id, {
                                                meraApiUrl: el.value.trim() || null,
                                              })
                                              setFeedback({ severity: 'success', message: 'تم حفظ عنوان Mera API' })
                                              await loadCustomerDetails(selectedCustomerId, false)
                                            } catch (error: unknown) {
                                              setFeedback({ severity: 'error', message: extractApiError(error, 'تعذر حفظ العنوان') })
                                            } finally {
                                              setSaving(false)
                                            }
                                          }}
                                        >
                                          حفظ
                                        </Button>
                                      </Stack>
                                    )}
                                    {(device.type === 'Mobile' || device.type === 'MeraApp') && selectedCustomerId && (
                                      <Box sx={{ mt: 1.5 }}>
                                        <FormControlLabel
                                          control={
                                            <Switch
                                              checked={!!device.useIndependentReportBranding}
                                              disabled={saving}
                                              onChange={async (e) => {
                                                try {
                                                  setSaving(true)
                                                  await deviceService.updateSiteSettings(device.id, {
                                                    useIndependentReportBranding: e.target.checked,
                                                  })
                                                  setFeedback({
                                                    severity: 'success',
                                                    message: e.target.checked
                                                      ? 'تم تفعيل إعدادات تقرير مستقلة — ارفع الصور أدناه'
                                                      : 'سيأخذ التطبيق صور التقارير من إعدادات العميل (نفس ميرا)',
                                                  })
                                                  await loadCustomerDetails(selectedCustomerId, false)
                                                } catch (error: unknown) {
                                                  setFeedback({ severity: 'error', message: extractApiError(error, 'تعذر حفظ الإعداد') })
                                                } finally {
                                                  setSaving(false)
                                                }
                                              }}
                                            />
                                          }
                                          label={device.useIndependentReportBranding ? 'صور تقارير خاصة بالجهاز' : 'نفس صور ميرا (العميل)'}
                                        />
                                        {device.useIndependentReportBranding && (
                                          <Box sx={{ mt: 1.5 }}>
                                            <ReportBrandingPanel
                                              headerUrl={device.reportHeaderImageUrl}
                                              footerUrl={device.reportFooterImageUrl}
                                              receiptUrl={device.receiptHeaderImageUrl}
                                              headerMm={device.reportHeaderHeightMm ?? 35}
                                              footerMm={device.reportFooterHeightMm ?? 25}
                                              receiptMm={device.receiptHeaderHeightMm ?? 30}
                                              cacheKey={`${device.id}-${device.hasReportHeaderImage}-${device.hasReportFooterImage}-${device.hasReceiptHeaderImage}`}
                                              disabled={saving}
                                              onUpload={async (kind, file) => {
                                                setSaving(true)
                                                try {
                                                  await deviceService.uploadReportImage(device.id, kind, file)
                                                  setFeedback({ severity: 'success', message: 'تم رفع الصورة — القديمة استُبدلت' })
                                                  await loadCustomerDetails(selectedCustomerId, false)
                                                } catch (error: unknown) {
                                                  setFeedback({ severity: 'error', message: extractApiError(error, 'تعذر رفع الصورة') })
                                                  throw error
                                                } finally {
                                                  setSaving(false)
                                                }
                                              }}
                                              onDelete={async (kind) => {
                                                setSaving(true)
                                                try {
                                                  await deviceService.deleteReportImage(device.id, kind)
                                                  setFeedback({ severity: 'success', message: 'تم حذف الصورة' })
                                                  await loadCustomerDetails(selectedCustomerId, false)
                                                } catch (error: unknown) {
                                                  setFeedback({ severity: 'error', message: extractApiError(error, 'تعذر حذف الصورة') })
                                                } finally {
                                                  setSaving(false)
                                                }
                                              }}
                                              onHeightChange={async (kind: BrandingKind, mm) => {
                                                setSaving(true)
                                                try {
                                                  await deviceService.updateSiteSettings(device.id, {
                                                    reportHeaderHeightMm: kind === 'header' ? mm : device.reportHeaderHeightMm ?? 35,
                                                    reportFooterHeightMm: kind === 'footer' ? mm : device.reportFooterHeightMm ?? 25,
                                                    receiptHeaderHeightMm: kind === 'receiptHeader' ? mm : device.receiptHeaderHeightMm ?? 30,
                                                  })
                                                  await loadCustomerDetails(selectedCustomerId, false)
                                                } catch (error: unknown) {
                                                  setFeedback({ severity: 'error', message: extractApiError(error, 'تعذر حفظ الارتفاع') })
                                                } finally {
                                                  setSaving(false)
                                                }
                                              }}
                                            />
                                          </Box>
                                        )}
                                      </Box>
                                    )}
                                    <Typography variant="body2" color="text.secondary">
                                      الاشتراك: {getSubscriptionLabel(device.subscriptionType, device.subscriptionDurationMonths)}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                      الحالة: {getSubscriptionStateLabel(device.subscriptionState)}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                      الانتهاء: {formatDate(device.subscriptionEndsAt)}
                                    </Typography>
                                    <Alert severity={device.lastUpdatedAt ? 'success' : 'warning'} sx={{ mt: 1.5, borderRadius: 2, py: 0.5 }}>
                                      <Typography variant="caption" sx={{ fontWeight: 800, display: 'block' }}>آخر تحديث (تاريخ ووقت)</Typography>
                                      <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                        {device.lastUpdatedAt ? formatDate(device.lastUpdatedAt) : 'لا يوجد تحديث مسجّل'}
                                      </Typography>
                                      <Typography variant="caption" color="text.secondary" display="block">
                                        بواسطة: {device.lastUpdatedByName || '—'}
                                        {device.lastUpdatedVersion ? ` · v${device.lastUpdatedVersion}` : ''}
                                      </Typography>
                                      {(device.recentUpdateLogs?.length ?? 0) > 0 &&
                                        device.recentUpdateLogs!.slice(0, 5).map((log) => (
                                          <Typography key={log.id} variant="caption" display="block" color="text.secondary">
                                            {formatDate(log.updatedAt)} — {log.updatedByName || '—'}
                                            {log.fromVersion || log.toVersion
                                              ? ` (${log.fromVersion || '?'} → ${log.toVersion || '?'})`
                                              : ''}
                                          </Typography>
                                        ))}
                                    </Alert>
                                    <Alert severity="info" sx={{ mt: 1.5, borderRadius: 2, py: 0.5 }}>
                                      <Typography variant="caption" sx={{ fontWeight: 800, display: 'block' }}>تفاصيل التفعيل</Typography>
                                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                        {device.notes?.trim() || 'لا توجد تفاصيل مسجّلة لهذا الجهاز.'}
                                      </Typography>
                                    </Alert>
                                    {isUnlinkedDevice(device) && selectedCustomerId && (
                                      <Button size="small" color="error" variant="outlined" startIcon={<Delete />} sx={{ mt: 1 }} onClick={() => deleteUnlinkedDevice(device, selectedCustomerId)}>
                                        حذف
                                      </Button>
                                    )}
                                  </Paper>
                                </Grid>
                              ))}
                            </Grid>
                          )}
                        </CardContent>
                      </Card>
                    )
                  })}
                </Stack>
              )}

              <Paper sx={{ p: 3, borderRadius: 3 }}>
                <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={2} sx={{ mb: 2 }}>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 800, mb: 1 }}>أخطاء العميل</Typography>
                    <Typography color="text.secondary">
                      يعرض الأخطاء المرتبطة بهذا العميل مع تحديد الجهاز الرئيسي أو الفرعي.
                    </Typography>
                  </Box>
                  <Chip label={`${customerErrors.length} خطأ`} color={customerErrors.length > 0 ? 'error' : 'success'} />
                </Stack>

                {customerErrors.length === 0 ? (
                  <Alert severity="success">لا توجد أخطاء مرتبطة بهذا العميل حالياً.</Alert>
                ) : (
                  <Stack spacing={1.5}>
                    {customerErrors.map((errorRow) => (
                      <Paper key={errorRow.errorId} sx={{ p: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                        <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={2}>
                          <Box>
                            <Typography sx={{ fontWeight: 700, mb: 0.5 }}>{errorRow.errorMessage}</Typography>
                            <Typography variant="body2" color="text.secondary">
                              {errorRow.deviceCode || errorRow.computerNo || '-'} | {errorRow.deviceType === 'Main'
                                ? 'جهاز رئيسي'
                                : errorRow.deviceType === 'Sub'
                                  ? `جهاز فرعي${errorRow.mainDeviceCode ? ` / الرئيسي: ${errorRow.mainDeviceCode}` : ''}`
                                  : 'غير مربوط'}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {errorRow.formOrEndpoint || errorRow.requestPath || 'بدون مسار'} - {formatDate(errorRow.errorTime)}
                            </Typography>
                          </Box>
                          <Stack direction={{ xs: 'row', md: 'column' }} spacing={1}>
                            <Chip label={errorRow.isFixed ? 'تم الإصلاح' : 'مفتوح'} color={errorRow.isFixed ? 'success' : 'error'} size="small" />
                            <Chip label={errorRow.isReportedToHaizoom ? 'تم التبليغ' : 'غير مبلغ'} variant="outlined" size="small" />
                          </Stack>
                        </Stack>
                      </Paper>
                    ))}
                  </Stack>
                )}
              </Paper>
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setDetailDialogOpen(false)}>إغلاق</Button>
        </DialogActions>
      </Dialog>

      <EditDeviceDialog
        open={!!editingDevice}
        device={editingDevice}
        customerId={selectedCustomerId || undefined}
        mainDevices={mainDevices}
        onClose={() => setEditingDevice(null)}
        onSaved={async () => {
          if (selectedCustomerId) await loadCustomerDetails(selectedCustomerId, false)
          setFeedback({ severity: 'success', message: 'تم حفظ تعديلات الجهاز' })
        }}
        onError={(message) => setFeedback({ severity: 'error', message })}
      />

      <Dialog open={deviceDialogOpen} onClose={() => setDeviceDialogOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>إضافة جهاز جديد</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Alert severity="info">مفتاح التفعيل يتولد تلقائياً من النظام. يمكنك ترك رمز الجهاز فارغاً.</Alert>
            <TextField select label="نوع الجهاز" value={deviceForm.type} onChange={(event) => {
              const nextType = event.target.value as DeviceFormState['type']
              setDeviceForm((prev) => ({
                ...prev,
                type: nextType,
                mainDeviceId: nextType === 'Main' ? '' : prev.mainDeviceId,
                meraApiUrl: isStockMeraApiUrl(prev.meraApiUrl)
                  ? defaultMeraApiUrlForType(nextType)
                  : prev.meraApiUrl,
              }))
            }} fullWidth>
              <MenuItem value="Main">جهاز رئيسي</MenuItem>
              <MenuItem value="Sub">جهاز فرعي</MenuItem>
              <MenuItem value="Mobile">موبايل Mera Route</MenuItem>
              <MenuItem value="MeraApp">Mera موبايل</MenuItem>
              <MenuItem value="Api">API</MenuItem>
            </TextField>

            {(deviceForm.type === 'Sub' || deviceForm.type === 'Mobile' || deviceForm.type === 'MeraApp' || deviceForm.type === 'Api') && (
              <TextField select label="الجهاز الرئيسي المرتبط" value={deviceForm.mainDeviceId} onChange={(event) => setDeviceForm((prev) => ({ ...prev, mainDeviceId: event.target.value }))} fullWidth>
                {mainDevices.map((device) => (
                  <MenuItem key={device.id} value={device.id}>{device.computerName || device.activationCode || device.deviceCode}</MenuItem>
                ))}
              </TextField>
            )}

            {/* عنوان سيرفر MeraSystemApi الذي يأخذه الجهاز من الموقع (قابل للتعديل) */}
            <TextField
              label="عنوان Mera API"
              value={deviceForm.meraApiUrl}
              onChange={(event) => setDeviceForm((prev) => ({ ...prev, meraApiUrl: event.target.value }))}
              fullWidth
              placeholder={defaultMeraApiUrlForType(deviceForm.type)}
              helperText={
                deviceForm.type === 'Mobile' || deviceForm.type === 'MeraApp'
                  ? 'عنوان IP الشبكة المحلية لسيرفر الـ API على الحاسبة (الموبايل لا يستخدم localhost). يمكنك تغييره.'
                  : deviceForm.type === 'Main'
                    ? 'عنوان سيرفر MeraSystemApi للحاسبة — الافتراضي localhost:5078. ليس 0.0.0.0 (ذلك لربط السيرفر فقط).'
                    : 'عنوان سيرفر MeraSystemApi — يمكنك تركه أو تعديله.'
              }
            />
            {deviceForm.type === 'Main' && (
              <>
                <Alert severity="success">تحديث: يمكنك إنشاء جهاز API مرتبط مع الرئيسي في نفس العملية.</Alert>
                <FormControlLabel
                  control={
                    <Switch
                      checked={deviceForm.createLinkedApi}
                      onChange={(e) => setDeviceForm((prev) => ({ ...prev, createLinkedApi: e.target.checked }))}
                    />
                  }
                  label="إنشاء جهاز API معه (رمز تفعيل API منفصل)"
                />
                {deviceForm.createLinkedApi && (
                  <>
                    <TextField
                      select
                      label="أين منتصب الـ API؟"
                      value={deviceForm.apiHostingKind}
                      onChange={(event) => setDeviceForm((prev) => ({
                        ...prev,
                        apiHostingKind: event.target.value as 'Windows' | 'Host',
                      }))}
                      fullWidth
                      helperText="الافتراضي ويندوز (منتصب على الرئيسية). غيّر إلى هوست فقط إذا أنت رفعته على Plesk."
                    >
                      <MenuItem value="Windows">ويندوز على الحاسبة الرئيسية</MenuItem>
                      <MenuItem value="Host">هوست (Plesk) — تحديث يدوي</MenuItem>
                    </TextField>
                    <TextField
                      label="رمز/باسوورد الداتابيس للـAPI"
                      value={deviceForm.dbPasswordOverride}
                      onChange={(event) => setDeviceForm((prev) => ({ ...prev, dbPasswordOverride: event.target.value }))}
                      fullWidth
                      type="password"
                      helperText="يُسلَّم للـAPI عند تفعيله. إن تُرك فارغاً يُستخدم الافتراضي من إعدادات الموقع."
                    />
                  </>
                )}
              </>
            )}
            {deviceForm.type === 'Api' && (
              <>
                <TextField
                  select
                  label="أين منتصب الـ API؟"
                  value={deviceForm.apiHostingKind}
                  onChange={(event) => setDeviceForm((prev) => ({
                    ...prev,
                    apiHostingKind: event.target.value as 'Windows' | 'Host',
                  }))}
                  fullWidth
                  helperText="الافتراضي ويندوز. غيّر إلى هوست فقط إذا أنت رفعته على Plesk."
                >
                  <MenuItem value="Windows">ويندوز على الحاسبة الرئيسية</MenuItem>
                  <MenuItem value="Host">هوست (Plesk) — تحديث يدوي</MenuItem>
                </TextField>
                <TextField
                  label="رمز/باسوورد الداتابيس"
                  value={deviceForm.dbPasswordOverride}
                  onChange={(event) => setDeviceForm((prev) => ({ ...prev, dbPasswordOverride: event.target.value }))}
                  fullWidth
                  type="password"
                  helperText="اختياري — وإلا الافتراضي من إعدادات الموقع"
                />
              </>
            )}
            {(deviceForm.type === 'Mobile' || deviceForm.type === 'MeraApp') && (
              <FormControlLabel
                control={
                  <Switch
                    checked={deviceForm.useIndependentReportBranding}
                    onChange={(e) => setDeviceForm((prev) => ({ ...prev, useIndependentReportBranding: e.target.checked }))}
                  />
                }
                label="صور تقارير مستقلة (وإلا نفس صور ميرا للعميل)"
              />
            )}

            <TextField label="الإصدار" value={deviceForm.version} onChange={(event) => setDeviceForm((prev) => ({ ...prev, version: event.target.value }))} fullWidth required />
            <TextField label="رمز الجهاز" value={deviceForm.deviceCode} onChange={(event) => setDeviceForm((prev) => ({ ...prev, deviceCode: event.target.value }))} helperText="اختياري: النظام يولد رمزاً داخلياً مؤقتاً إذا بقي فارغاً." fullWidth />
            <TextField
              label="تفاصيل التفعيل"
              value={deviceForm.notes}
              onChange={(event) => setDeviceForm((prev) => ({ ...prev, notes: event.target.value }))}
              multiline
              minRows={2}
              fullWidth
              helperText="تظهر هذه التفاصيل دائماً مع الجهاز بعد الإنشاء (ملاحظات فنية، موقع الجهاز، شروط خاصة...)."
            />
            <TextField
              select
              label="نوع الاشتراك"
              value={deviceForm.subscriptionType}
              onChange={(event) =>
                setDeviceForm((prev) => ({
                  ...prev,
                  subscriptionType: event.target.value as 'Trial' | 'Monthly' | 'Yearly' | 'Permanent',
                }))
              }
              fullWidth
            >
              <MenuItem value="Trial">تجريبي</MenuItem>
              <MenuItem value="Monthly">شهري</MenuItem>
              <MenuItem value="Yearly">سنوي</MenuItem>
              <MenuItem value="Permanent">دائمي مفتوح</MenuItem>
            </TextField>
            {deviceForm.subscriptionType === 'Trial' && (
              <TextField
                select
                label="مدة التجريبي"
                value={deviceForm.subscriptionDurationMonths}
                onChange={(event) =>
                  setDeviceForm((prev) => ({
                    ...prev,
                    subscriptionDurationMonths: Number(event.target.value),
                  }))
                }
                fullWidth
              >
                <MenuItem value={1}>شهر واحد</MenuItem>
                <MenuItem value={2}>شهرين</MenuItem>
                <MenuItem value={3}>ثلاثة أشهر</MenuItem>
              </TextField>
            )}
            <Alert severity="warning">
              يبدأ الاشتراك عند أول تفعيل للجهاز، والتنبيه قبل الانتهاء بـ 3 أيام، وفترة السماح 3 أيام.
            </Alert>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2.5 }}>
          <Button onClick={() => setDeviceDialogOpen(false)}>إلغاء</Button>
          <Button variant="contained" onClick={submitDevice} disabled={saving}>{saving ? 'جاري الإنشاء...' : 'إنشاء الجهاز'}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
